import type { CategoryKind } from "@instead/shared";

/** Line icons on a 20-unit grid, currentColor, decorative unless labelled. */
function I({ d, size = 18, label, fill }: { d: React.ReactNode; size?: number; label?: string; fill?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill={fill ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={label ? undefined : true}
      role={label ? "img" : undefined}
      focusable="false"
    >
      {label && <title>{label}</title>}
      {d}
    </svg>
  );
}

type P = { size?: number; label?: string };

export const Ic = {
  rest: (p: P = {}) => <I {...p} d={<><rect x="3" y="5" width="10" height="13" rx="2" /><path d="M7 2.8 15.2 5l-2.4 9" /></>} />,
  shelf: (p: P = {}) => <I {...p} d={<><path d="M3 16.5h14" /><rect x="4" y="6" width="3" height="8" rx="0.8" /><rect x="9" y="4" width="3" height="10" rx="0.8" /><path d="m14.2 5.4 2.3 8.6" /></>} />,
  settings: (p: P = {}) => <I {...p} d={<><path d="M3 6h9M15.5 6H17M3 14h2.5M9 14h8" /><circle cx="13" cy="6" r="2" /><circle cx="7" cy="14" r="2" /></>} />,
  back: (p: P = {}) => <I {...p} d={<path d="M12.5 4.5 7 10l5.5 5.5" />} />,
  chevron: (p: P = {}) => <I {...p} d={<path d="M7.5 4.5 13 10l-5.5 5.5" />} />,
  check: (p: P = {}) => <I {...p} d={<path d="M4 10.5l4 4 8-9" />} />,
  x: (p: P = {}) => <I {...p} d={<path d="M5 5l10 10M15 5L5 15" />} />,
  plus: (p: P = {}) => <I {...p} d={<path d="M10 4v12M4 10h12" />} />,
  edit: (p: P = {}) => <I {...p} d={<path d="M13.5 3.5l3 3L7 16l-3.7.7L4 13z" />} />,
  trash: (p: P = {}) => <I {...p} d={<path d="M4 6h12M8 6V4.5A1 1 0 019 3.5h2a1 1 0 011 1V6m3 0-.7 9.5A1.5 1.5 0 0112.8 17H7.2a1.5 1.5 0 01-1.5-1.5L5 6z" />} />,
  refresh: (p: P = {}) => <I {...p} d={<path d="M16 10a6 6 0 11-1.8-4.3M16 3.5v3h-3" />} />,
  drag: (p: P = {}) => <I {...p} d={<path d="M4 6.5h12M4 10h12M4 13.5h12" />} />,
  external: (p: P = {}) => <I {...p} d={<><path d="M8 4H5a1.5 1.5 0 00-1.5 1.5V15A1.5 1.5 0 005 16.5h9.5A1.5 1.5 0 0016 15v-3" /><path d="M12 3.5h4.5V8M16 4L9.5 10.5" /></>} />,
  link: (p: P = {}) => <I {...p} d={<><path d="M8.5 11.5a3 3 0 004.2 0l2.6-2.6a3 3 0 00-4.2-4.2L9.8 6" /><path d="M11.5 8.5a3 3 0 00-4.2 0L4.7 11.1a3 3 0 004.2 4.2L10.2 14" /></>} />,
  locate: (p: P = {}) => <I {...p} d={<><circle cx="10" cy="10" r="5.5" /><circle cx="10" cy="10" r="1.6" /><path d="M10 2v2.5M10 15.5V18M2 10h2.5M15.5 10H18" /></>} />,
  pin: (p: P = {}) => <I {...p} d={<path d="M10 3l2 4.3 4.7.5-3.5 3.2 1 4.6L10 13.2l-4.2 2.4 1-4.6L3.3 7.8 8 7.3z" />} />,
  pinFilled: (p: P = {}) => <I {...p} fill d={<path d="M10 3l2 4.3 4.7.5-3.5 3.2 1 4.6L10 13.2l-4.2 2.4 1-4.6L3.3 7.8 8 7.3z" />} />,
  search: (p: P = {}) => <I {...p} d={<><circle cx="9" cy="9" r="5.5" /><path d="m13.5 13.5 3.5 3.5" /></>} />,
  info: (p: P = {}) => <I {...p} d={<><circle cx="10" cy="10" r="7" /><path d="M10 9.2v4.3M10 6.6v.3" /></>} />,
  camera: (p: P = {}) => <I {...p} d={<><rect x="2.5" y="5.5" width="15" height="11" rx="2.5" /><circle cx="10" cy="11" r="3" /><path d="M7 5.5l1.2-2h3.6L13 5.5" /></>} />,
  library: (p: P = {}) => <I {...p} d={<><rect x="3" y="3.5" width="14" height="13" rx="2.5" /><circle cx="7.5" cy="8" r="1.4" /><path d="M3.5 14.5l4-4 3 3 2.5-2.5 3.5 3.5" /></>} />,
  smile: (p: P = {}) => <I {...p} d={<><circle cx="10" cy="10" r="7" /><path d="M7 12c.8 1.2 1.8 1.8 3 1.8s2.2-.6 3-1.8" /><path d="M7.5 8h.01M12.5 8h.01" /></>} />,
  text: (p: P = {}) => <I {...p} d={<path d="M4 5h12M10 5v11M7 16h6" />} />,
  none: (p: P = {}) => <I {...p} d={<><rect x="3" y="4" width="14" height="12" rx="2.5" /><path d="M4 15l12-10" /></>} />,
  place: (p: P = {}) => <I {...p} d={<><path d="M10 18s-5.5-5.2-5.5-9.3A5.5 5.5 0 0110 3a5.5 5.5 0 015.5 5.7C15.5 12.8 10 18 10 18z" /><circle cx="10" cy="8.6" r="1.8" /></>} />,
  copy: (p: P = {}) => <I {...p} d={<><rect x="7" y="7" width="10" height="10" rx="2" /><path d="M13 7V5a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2" /></>} />,
  lock: (p: P = {}) => <I {...p} d={<><rect x="4.5" y="9" width="11" height="8" rx="2" /><path d="M7 9V6.5a3 3 0 016 0V9" /></>} />,
  up: (p: P = {}) => <I {...p} d={<path d="M4.5 12.5 10 7l5.5 5.5" />} />,
  down: (p: P = {}) => <I {...p} d={<path d="M4.5 7.5 10 13l5.5-5.5" />} />,
  bug: (p: P = {}) => <I {...p} d={<><rect x="6" y="7" width="8" height="10" rx="4" /><path d="M8 7V5.5a2 2 0 014 0V7M3 11h3M14 11h3M4 16l2.5-1.5M16 16l-2.5-1.5M4.5 6.5 7 8M15.5 6.5 13 8" /></>} />,
};

const CAT: Record<CategoryKind, React.ReactNode> = {
  book: <><path d="M4 3.5h8a3 3 0 013 3V17H7a3 3 0 00-3 3z M4 3.5V20" /><path d="M7 7h5" /></>,
  video: <><rect x="2.5" y="4" width="15" height="12" rx="2.5" /><path d="M8.5 7.5l4 2.5-4 2.5z" /></>,
  article: <><rect x="4" y="2.5" width="12" height="15" rx="2" /><path d="M7 6.5h6M7 10h6M7 13.5h4" /></>,
  listen: <><path d="M4 13v-2a6 6 0 0112 0v2" /><rect x="3" y="12.5" width="3.5" height="5" rx="1.5" /><rect x="13.5" y="12.5" width="3.5" height="5" rx="1.5" /></>,
  hobby: <><path d="M15.5 3.5l1.5 1.5-8 8-2.5 1 1-2.5z" /><path d="M5 17c1.5 0 2.5-1 2.5-2.5" /></>,
  activity: <><path d="M10 17c4-2 6-5.5 6-9.5 0-1.5-.5-3-1.5-4C10 5 6 8.5 6 13c0 1.6.7 3 1.8 4z" /><path d="M10 17c0-4 1.5-7.5 4.5-10" /></>,
  custom: <><rect x="3" y="3" width="6" height="6" rx="1.5" /><rect x="11" y="3" width="6" height="6" rx="1.5" /><rect x="3" y="11" width="6" height="6" rx="1.5" /><rect x="11" y="11" width="6" height="6" rx="1.5" /></>,
};

export function CatIcon({ kind, size = 18 }: { kind: CategoryKind | "all" | "unsorted"; size?: number }) {
  if (kind === "all") return <I size={size} d={CAT.custom} />;
  if (kind === "unsorted") return <I size={size} d={<><rect x="3" y="5" width="14" height="11" rx="2" /><path d="M3 8h14M8 5V3.5h4V5" /></>} />;
  return <I size={size} d={CAT[kind]} />;
}
